/* ==========================================================================
   Coast theme — Comfortable & Coast (Co&Co)
   Vanilla JS, custom elements, no dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const CoCo = (window.CoCo = window.CoCo || {});
  const routes = CoCo.routes || {};
  const settings = CoCo.settings || {};
  const strings = CoCo.strings || {};

  /* ---------- Utilities ---------- */
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  const storage = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* noop */ } }
  };

  CoCo.formatMoney = function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = format || settings.moneyFormat || '${{amount}}';
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = fmt.match(pattern);
    let value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(pattern, value);
  };

  const toastEl = () => qs('[data-toast]');
  let toastTimer;
  CoCo.toast = (message) => {
    const el = toastEl();
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 300);
    }, 2600);
  };

  /* ---------- Focus trap ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const nodes = qsa(FOCUSABLE, container).filter((n) => n.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------- Drawer ---------- */
  class CocoDrawer extends HTMLElement {
    connectedCallback() {
      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
        trapFocus(this, e);
      };
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      const focusTarget = qs('[data-autofocus]', this) || qs(FOCUSABLE, qs('.drawer__panel', this) || this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 80);
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!qs('coco-drawer.is-open')) document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.trigger.focus) this.trigger.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  customElements.define('coco-drawer', CocoDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
    }
  });

  /* ---------- Cart ---------- */
  const Cart = {
    sectionsToRender() {
      const ids = [];
      if (qs('#CartDrawer')) ids.push('cart-drawer');
      qsa('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
      return [...new Set(ids)];
    },
    async add(formData) {
      const sections = this.sectionsToRender();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || strings.cartError);
      }
      this.renderSections(data.sections);
      await this.refreshCount();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      this.afterAdd(data);
      return data;
    },
    async change(line, quantity) {
      const sections = this.sectionsToRender();
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      this.renderSections(data.sections);
      this.setCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      if (window.upcartRefreshCart) { try { window.upcartRefreshCart(); } catch (err) { /* noop */ } }
      return data;
    },
    async updateNote(note) {
      await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },
    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        qsa(`[data-render-section="${id}"]`).forEach((target) => {
          const key = target.dataset.renderKey;
          const source = key ? qs(`[data-render-section="${id}"][data-render-key="${key}"]`, doc) : qs(`[data-render-section="${id}"]`, doc);
          if (source) target.innerHTML = source.innerHTML;
        });
      });
    },
    async refreshCount() {
      try {
        const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.setCount(cart.item_count);
        return cart;
      } catch (e) { return null; }
    },
    setCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? count : '';
        el.dataset.count = count;
      });
    },
    open() {
      const type = settings.cartType;
      if (type === 'upcart' && typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
        return true;
      }
      const drawer = qs('#CartDrawer');
      if (drawer && (type === 'drawer' || type === 'upcart')) { drawer.open(); return true; }
      return false;
    },
    afterAdd() {
      const type = settings.cartType;
      if (type === 'upcart') {
        if (typeof window.upcartRefreshCart === 'function') { try { window.upcartRefreshCart(); } catch (e) { /* noop */ } }
        if (typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); return; }
        if (this.open()) return;
        CoCo.toast(strings.added);
        return;
      }
      if (type === 'drawer' && this.open()) return;
      if (type === 'page') { window.location.href = routes.cart; return; }
      CoCo.toast(strings.added);
    }
  };
  CoCo.Cart = Cart;

  // Header bag icon
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-toggle]');
    if (!link) return;
    if (settings.cartType === 'page') return;
    if (Cart.open()) e.preventDefault();
  });

  /* ---------- Product form ---------- */
  // Personalization fields (e.g. Easify Product Options) live in an app block outside the
  // <form>, and the app may link them to the wrong form. Collect every properties[...] field
  // in the product section, validate required ones, and add them to the cart request.
  const PROP_SELECTOR = '[name^="properties["]';
  const PERSONALIZE_ERROR = 'Please fill in this field to personalize your piece.';

  function productScope(form) {
    return form.closest('.shopify-section') || form.closest('main') || document;
  }

  function personalizationFields(form) {
    if (!form.matches('[data-product-form]')) return [];
    return qsa(PROP_SELECTOR, productScope(form)).filter((el) => {
      if (el.disabled) return false;
      const host = el.closest('form');
      return !host || host === form; // skip fields that belong to another product form (e.g. quick add)
    });
  }

  function isFieldHidden(el) {
    if (el.closest('.tpo_hidden, [hidden]')) return true;
    return el.type !== 'hidden' && el.offsetParent === null && el.getClientRects().length === 0;
  }

  function isFieldRequired(el) {
    return el.required || el.dataset.required === '1' || el.dataset.required === 'true' || el.getAttribute('aria-required') === 'true';
  }

  function fieldHasValue(el, scope) {
    if (el.type === 'checkbox' || el.type === 'radio') {
      return qsa(`[name="${CSS.escape(el.name)}"]`, scope).some((r) => r.checked);
    }
    if (el.type === 'file') return !!(el.files && el.files.length);
    return String(el.value || '').trim() !== '';
  }

  function setFieldError(el, message) {
    const box = el.closest('.tpo_option-container');
    const msg = box ? qs('.tpo_error-message', box) : null;
    if (box) box.classList.toggle('is-invalid', !!message);
    el.classList.toggle('is-invalid', !!message);
    if (message) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid');
    if (msg) { msg.textContent = message || ''; msg.classList.toggle('is-visible', !!message); }
    return !!msg;
  }

  function validatePersonalization(form, fields) {
    const scope = productScope(form);
    let firstInvalid = null;
    const seen = new Set();
    fields.forEach((el) => {
      if (seen.has(el.name) && (el.type === 'checkbox' || el.type === 'radio')) return;
      seen.add(el.name);
      if (isFieldHidden(el)) return;
      let invalid = isFieldRequired(el) && !fieldHasValue(el, scope);
      if (!invalid && typeof el.checkValidity === 'function' && el.type !== 'hidden' && !el.checkValidity()) invalid = true;
      setFieldError(el, invalid ? PERSONALIZE_ERROR : '');
      if (invalid && !firstInvalid) firstInvalid = el;
    });
    return firstInvalid;
  }

  function appendPersonalization(fd, form, fields) {
    fields.forEach((el) => {
      if (el.form === form) return; // already included by FormData
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      if (el.type === 'file') { if (el.files && el.files[0]) fd.append(el.name, el.files[0]); return; }
      if (el.tagName === 'SELECT' && el.multiple) {
        const vals = Array.from(el.selectedOptions).map((o) => o.value).filter(Boolean);
        if (vals.length) fd.set(el.name, vals.join(', '));
        return;
      }
      if (el.value === '' || el.value == null) return;
      if (el.type === 'checkbox' && fd.has(el.name)) fd.set(el.name, `${fd.get(el.name)}, ${el.value}`);
      else fd.set(el.name, el.value);
    });
    // Drop blank properties (e.g. conditional fields that were not shown) so the cart stays clean.
    Array.from(fd.keys()).forEach((key) => {
      if (key.indexOf('properties[') !== 0) return;
      const vals = fd.getAll(key);
      if (vals.every((v) => typeof v === 'string' && v.trim() === '')) fd.delete(key);
    });
  }

  // Point app fields at the main product form so native submits include them too.
  function bindPersonalization(form) {
    const formId = form.getAttribute('id'); // not form.id: the hidden input name="id" shadows it
    if (!formId) return;
    qsa(`${PROP_SELECTOR}, .easify-product-options [form]`, productScope(form)).forEach((el) => {
      if (el.closest('form')) return;
      if (el.getAttribute('form') !== formId) el.setAttribute('form', formId);
    });
  }

  function initPersonalization() {
    qsa('form[data-product-form]').forEach((form) => {
      const scope = productScope(form);
      bindPersonalization(form);
      if (scope.dataset.personalizationObserved) return;
      scope.dataset.personalizationObserved = 'true';
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; bindPersonalization(form); });
      }).observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['form'] });
      const clear = (e) => {
        const el = e.target;
        if (el.matches && el.matches(PROP_SELECTOR) && el.getAttribute('aria-invalid') === 'true' && fieldHasValue(el, scope)) setFieldError(el, '');
      };
      scope.addEventListener('input', clear);
      scope.addEventListener('change', clear);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPersonalization);
  else initPersonalization();
  document.addEventListener('shopify:section:load', initPersonalization);

  async function submitAddForm(form, submitter) {
    const button = submitter && submitter.type === 'submit' ? submitter : qs('[type="submit"]', form);
    const errorEl = qs('[data-form-error]', form.closest('[data-product-form-wrap]') || form);
    const personalFields = personalizationFields(form);
    const invalidField = validatePersonalization(form, personalFields);
    if (invalidField) {
      invalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => { try { invalidField.focus({ preventScroll: true }); } catch (e) { /* noop */ } }, 350);
      if (!invalidField.closest('.tpo_option-container') && errorEl) { errorEl.textContent = PERSONALIZE_ERROR; errorEl.hidden = false; }
      return;
    }
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-disabled', 'true'); }
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    try {
      const fd = new FormData(form);
      appendPersonalization(fd, form, personalFields);
      if (submitter && submitter.name && submitter.value) fd.set(submitter.name, submitter.value);
      if (submitter && submitter.dataset && submitter.dataset.quickVariant) fd.set('id', submitter.dataset.quickVariant);
      await Cart.add(fd);
      const wrap = form.closest('[data-quick-add-wrap]');
      if (wrap) wrap.classList.remove('is-open');
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      else CoCo.toast(err.message);
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-disabled'); }
    }
  }

  // Quick-add size buttons: write the chosen variant into the form's hidden id field before
  // the form submits, so the add works however the submit is handled (theme, Upcart or native).
  document.addEventListener('click', (e) => {
    const sizeBtn = e.target.closest('[data-quick-variant]');
    if (!sizeBtn || !sizeBtn.form) return;
    const idInput = sizeBtn.form.querySelector('[data-quick-add-id]');
    if (idInput) idInput.value = sizeBtn.dataset.quickVariant;
  }, true);

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-product-form], [data-quick-add]')) {
      e.preventDefault();
      submitAddForm(form, e.submitter);
    }
  });

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-quick-add-toggle]');
    if (toggle) {
      e.preventDefault();
      const wrap = toggle.closest('[data-quick-add-wrap]');
      const open = !wrap.classList.contains('is-open');
      qsa('[data-quick-add-wrap].is-open').forEach((w) => w.classList.remove('is-open'));
      wrap.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (!e.target.closest('[data-quick-add-wrap]')) {
      qsa('[data-quick-add-wrap].is-open').forEach((w) => w.classList.remove('is-open'));
    }
  });

  /* ---------- Quantity ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[name]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        let val = parseInt(this.input.value, 10) || 0;
        val = btn.name === 'plus' ? Math.min(val + step, max) : Math.max(val - step, min);
        if (String(val) !== this.input.value) {
          this.input.value = val;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart items (drawer + page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = debounce((input) => this.update(input.dataset.line, parseInt(input.value, 10)), 300);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-line]')) this.onChange(e.target);
        if (e.target.matches('[data-cart-note]')) Cart.updateNote(e.target.value);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (remove) { e.preventDefault(); this.update(remove.dataset.removeLine, 0); }
      });
    }
    async update(line, quantity) {
      this.classList.add('is-loading');
      try { await Cart.change(parseInt(line, 10), quantity); }
      catch (err) { CoCo.toast(err.message); }
      finally { this.classList.remove('is-loading'); }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = qs('script[data-variants]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.sectionId = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    getSelected() {
      return qsa('[data-option-group]', this).map((group) => {
        const checked = qs('input:checked', group);
        const select = qs('select', group);
        return checked ? checked.value : select ? select.value : null;
      });
    }
    onChange() {
      const selected = this.getSelected();
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      qsa('[data-option-group]', this).forEach((group, i) => {
        const label = qs('[data-selected-value]', group);
        if (label) label.textContent = selected[i];
      });
      this.updateAvailability();
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: this.sectionId, variant } }));
      this.render(variant);
    }
    updateAvailability() {
      const selected = this.getSelected();
      qsa('[data-option-group]', this).forEach((group, index) => {
        qsa('input[type="radio"]', group).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((opt, i) => opt === candidate[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
    render(variant) {
      const scope = document.getElementById(`ProductSection-${this.sectionId}`) || document;
      const buttons = qsa(`[data-atc-button="${this.sectionId}"]`);
      const idInputs = qsa(`[data-variant-id="${this.sectionId}"]`);
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; qs('[data-atc-label]', b).textContent = strings.unavailable; });
        return;
      }
      idInputs.forEach((input) => { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); });
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const label = qs('[data-atc-label]', b);
        if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      });
      qsa(`[data-price-wrap="${this.sectionId}"]`).forEach((wrap) => {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        const priceEl = qs('.price', wrap);
        if (!priceEl) return;
        priceEl.classList.toggle('price--on-sale', !!onSale);
        const current = qs('.price__current', priceEl);
        if (current) current.textContent = CoCo.formatMoney(variant.price);
        let compare = qs('.price__compare', priceEl);
        let badge = qs('.badge--sale', priceEl);
        if (onSale) {
          if (!compare) { compare = document.createElement('s'); compare.className = 'price__compare'; current.after(compare); }
          compare.textContent = CoCo.formatMoney(variant.compare_at_price);
          if (badge) badge.textContent = `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
        } else {
          if (compare) compare.remove();
          if (badge) badge.remove();
        }
      });
      qsa('[data-variant-sku]', scope).forEach((el) => { el.textContent = variant.sku || ''; });
      const stock = qs('[data-stock-note]', scope);
      if (stock) stock.hidden = !variant.available;
      if (variant.featured_media) {
        const gallery = qs('media-gallery', scope);
        if (gallery && gallery.goTo) gallery.goTo(variant.featured_media.id, true);
      }
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-gallery-track]', this);
      this.items = qsa('[data-media-id]', this.track);
      this.dots = qsa('[data-gallery-dot]', this);
      this.thumbs = qsa('[data-gallery-thumb]', this);
      this.counter = qs('[data-gallery-counter]', this);
      this.progress = qs('[data-gallery-progress]', this);
      if (!this.track) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.items.indexOf(entry.target), false);
        });
      }, { root: this.track, threshold: [0.6] });
      this.items.forEach((item) => this.observer.observe(item));

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.scrollToIndex(i)));
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => { this.setActive(i, true); }));
      this.track.addEventListener('click', (e) => {
        const item = e.target.closest('[data-media-id]');
        if (!item || item.dataset.mediaType !== 'image') return;
        const lightbox = qs(`#Lightbox-${this.dataset.section}`);
        if (lightbox) lightbox.open(this.items.indexOf(item));
      });
    }
    isSlider() { return window.matchMedia('(max-width: 989px)').matches; }
    setActive(index, scrollThumb) {
      if (index < 0) return;
      this.items.forEach((el, i) => el.classList.toggle('is-active', i === index));
      this.dots.forEach((el, i) => el.classList.toggle('is-active', i === index));
      this.thumbs.forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.items.length}`;
      if (this.progress) this.progress.style.width = `${((index + 1) / this.items.length) * 100}%`;
      if (scrollThumb && this.thumbs[index]) this.thumbs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }
    goTo(mediaId) {
      const index = this.items.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (this.isSlider()) { this.scrollToIndex(index); this.setActive(index, false); return; }
      if (this.classList.contains('gallery--thumbs')) { this.setActive(index, true); return; }
      // Stacked/grid layout: move the variant image to the front.
      const item = this.items[index];
      if (index !== 0) {
        this.track.prepend(item);
        this.items = qsa('[data-media-id]', this.track);
        this.items.forEach((el, i) => el.classList.toggle('gallery__item--featured', i === 0 && this.dataset.layout === 'grid'));
      }
      const rect = this.getBoundingClientRect();
      if (rect.top < 0) window.scrollTo({ top: window.scrollY + rect.top - 120, behavior: 'smooth' });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class LightboxModal extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-lightbox-track]', this);
      this.onKey = (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      };
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-lightbox-close]') || e.target.matches('img')) this.close();
        const nav = e.target.closest('[data-lightbox-step]');
        if (nav) this.step(parseInt(nav.dataset.lightboxStep, 10));
      });
    }
    open(index = 0) {
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      qsa('img[data-src]', this).forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      requestAnimationFrame(() => { this.track.scrollLeft = this.track.clientWidth * index; });
      document.addEventListener('keydown', this.onKey);
      const btn = qs('[data-lightbox-close]', this);
      if (btn) btn.focus();
    }
    step(dir) { this.track.scrollBy({ left: this.track.clientWidth * dir, behavior: 'smooth' }); }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKey);
    }
  }
  customElements.define('lightbox-modal', LightboxModal);

  /* ---------- Tabs ---------- */
  class CocoTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.panels = qsa('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        this.panels[i].hidden = !on;
      });
      this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
  }
  customElements.define('coco-tabs', CocoTabs);

  /* ---------- Slider ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slider-track]', this);
      if (!this.track) return;
      this.prev = qs('[data-slider-prev]', this);
      this.next = qs('[data-slider-next]', this);
      this.bar = qs('[data-slider-progress] span', this);
      const update = () => this.update();
      this.track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      setTimeout(update, 50);
    }
    go(dir) {
      const slide = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
      const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: dir * step * visible, behavior: 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = pos <= 4;
      if (this.next) this.next.disabled = pos >= max - 4;
      if (this.bar) {
        const ratio = this.track.clientWidth / this.track.scrollWidth;
        this.bar.style.width = `${Math.min(100, ratio * 100)}%`;
        this.bar.style.transform = `translateX(${max > 0 ? (pos / max) * ((1 / ratio) - 1) * 100 : 0}%)`;
      }
      this.classList.toggle('is-static', max <= 4);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = qsa('[data-announcement]', this);
      this.index = 0;
      if (this.messages.length < 2) return;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      qsa('[data-announcement-step]', this).forEach((b) => b.addEventListener('click', () => { this.go(parseInt(b.dataset.announcementStep, 10)); this.play(); }));
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.play());
      this.play();
    }
    play() { clearInterval(this.timer); this.timer = setInterval(() => this.go(1), this.speed); }
    go(dir) {
      this.messages[this.index].classList.remove('is-active');
      this.messages[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + dir + this.messages.length) % this.messages.length;
      this.messages[this.index].classList.add('is-active');
      this.messages[this.index].setAttribute('aria-hidden', 'false');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.section-header-wrapper') || this;
      const onScroll = () => {
        const scrolled = window.scrollY > 8;
        this.wrapper.classList.toggle('is-scrolled', scrolled);
        const h = this.wrapper.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-offset', `${Math.round(Math.max(0, h))}px`);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      // keyboard support for mega menus
      qsa('.main-nav__item', this).forEach((item) => {
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { item.classList.remove('is-open'); qs('a', item).focus(); } });
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.defaults = qs('[data-search-defaults]', this);
      if (!this.input || !this.results || this.dataset.enabled === 'false') return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        if (this.defaults) this.defaults.hidden = false;
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=query,product,collection,page,article&resources[limit]=8&resources[limit_scope]=each`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const content = qs('#shopify-section-predictive-search', doc) || doc.body;
        this.results.innerHTML = content.innerHTML;
        this.results.hidden = false;
        if (this.defaults) this.defaults.hidden = true;
      } catch (e) { /* aborted */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Collection filters ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onChange = debounce(() => this.apply(), 400);
      this.addEventListener('change', (e) => {
        if (e.target.closest('form[data-facets-form]') || e.target.matches('[data-sort]')) this.onChange();
        if (e.target.matches('[data-density-toggle]')) return;
      });
      this.addEventListener('submit', (e) => { if (e.target.matches('form[data-facets-form]')) { e.preventDefault(); this.apply(); } });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.render(new URL(link.href, window.location.origin).searchParams.toString()); }
        const density = e.target.closest('[data-density]');
        if (density) {
          const grid = qs('[data-collection-grid]', this);
          if (grid) grid.dataset.density = density.dataset.density;
          qsa('[data-density]', this).forEach((b) => b.classList.toggle('is-active', b === density));
          storage.set('coco:density', density.dataset.density);
        }
      });
      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), false));
      const saved = storage.get('coco:density');
      if (saved) {
        const btn = qs(`[data-density="${saved}"]`, this);
        if (btn) btn.click();
      }
    }
    params() {
      const forms = qsa('form[data-facets-form]', this);
      const params = new URLSearchParams();
      forms.forEach((form) => {
        new FormData(form).forEach((value, key) => {
          if (value === '') return;
          if (!params.getAll(key).includes(value)) params.append(key, value);
        });
      });
      const sort = qs('[data-sort]', this);
      if (sort) params.set('sort_by', sort.value);
      return params.toString();
    }
    apply() { this.render(this.params()); }
    async render(search, push = true) {
      const wrap = qs('[data-collection-results]', this);
      if (wrap) wrap.classList.add('is-loading');
      const url = `${window.location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const res = await fetch(url);
        const doc = parseHTML(await res.text());
        ['[data-collection-results]', '[data-facets-body]', '[data-active-filters]', '[data-product-count]'].forEach((sel) => {
          const src = qs(sel, doc);
          const dest = qs(sel, this);
          if (src && dest) dest.innerHTML = src.innerHTML;
        });
        const grid = qs('[data-collection-grid]', this);
        const saved = storage.get('coco:density');
        if (grid && saved) grid.dataset.density = saved;
        if (push) window.history.pushState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
      } catch (e) {
        window.location.search = search;
      } finally {
        const w = qs('[data-collection-results]', this);
        if (w) w.classList.remove('is-loading');
        reveal();
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-load-more]');
        if (!btn) return;
        e.preventDefault();
        btn.classList.add('is-loading');
        try {
          const res = await fetch(btn.dataset.url || btn.href);
          const doc = parseHTML(await res.text());
          const grid = qs('[data-collection-grid]');
          const incoming = qs('[data-collection-grid]', doc);
          if (grid && incoming) qsa(':scope > *', incoming).forEach((n) => grid.appendChild(n));
          const nextLoader = qs('load-more', doc);
          this.innerHTML = nextLoader ? nextLoader.innerHTML : '';
          reveal();
        } catch (err) {
          window.location.href = btn.href;
        }
      });
    }
  }
  customElements.define('load-more', LoadMore);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      const footer = qs('.site-footer');
      let pastTarget = false;
      let nearFooter = false;
      const toggle = () => this.classList.toggle('is-visible', pastTarget && !nearFooter);
      new IntersectionObserver(([entry]) => {
        pastTarget = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        toggle();
      }).observe(target);
      if (footer) new IntersectionObserver(([entry]) => { nearFooter = entry.isIntersecting; toggle(); }).observe(footer);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sticky-submit]');
        if (!btn) return;
        e.preventDefault();
        const form = document.getElementById(this.dataset.form);
        if (form) {
          if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const rec = qs('product-recommendations', doc);
          if (rec && rec.innerHTML.trim().length && qs('[data-has-products]', rec)) {
            this.innerHTML = rec.innerHTML;
            this.hidden = false;
            const wrap = this.closest('[data-recommendations-wrap]');
            if (wrap) wrap.hidden = false;
            reveal();
          } else if (this.dataset.fallback === 'hide') {
            const wrap = this.closest('[data-recommendations-wrap]');
            if (wrap) wrap.hidden = true;
          }
        } catch (e) { /* ignore */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 600px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Localization ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]')) {
      const form = e.target.closest('form');
      if (form) form.submit();
    }
  });

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  function reveal() {
    if (!document.body.classList.contains('has-animations')) return;
    if (!('IntersectionObserver' in window)) {
      qsa('.reveal, .reveal-stagger').forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); revealObserver.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    qsa('.reveal:not(.is-revealed), .reveal-stagger:not(.is-revealed)').forEach((el) => revealObserver.observe(el));
  }
  CoCo.reveal = reveal;

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', () => reveal());
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target;
    const track = slide.closest('[data-slider-track]');
    if (track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    const message = slide.closest('[data-announcement]');
    if (message) {
      qsa('[data-announcement]', message.parentElement).forEach((m) => m.classList.toggle('is-active', m === message));
    }
  });

  /* ---------- Init ---------- */
  const init = () => {
    reveal();
    // Upcart: keep theme counters in sync with app-driven updates
    if (typeof window.upcartSubscribeCartUpdated === 'function' && !CoCo.upcartBound) {
      CoCo.upcartBound = true;
      try { window.upcartSubscribeCartUpdated(() => Cart.refreshCount()); } catch (e) { /* noop */ }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', () => {
    if (typeof window.upcartSubscribeCartUpdated === 'function' && !CoCo.upcartBound) {
      CoCo.upcartBound = true;
      try { window.upcartSubscribeCartUpdated(() => Cart.refreshCount()); } catch (e) { /* noop */ }
    }
  });
})();
